import React, { useState } from 'react';
import FormField from './FormField';

const FormFieldDemo: React.FC = () => {
  const [formData, setFormData] = useState({
    farmName: '',
    email: '',
    password: '',
    phone: '',
    waterTemp: '',
    species: '',
    description: '',
    feedType: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleFieldChange = (field: string) => (value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.farmName) newErrors.farmName = 'Facility name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!formData.email.includes('@')) newErrors.email = 'Please enter a valid email';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const speciesOptions = [
    { value: 'salmon', label: 'Atlantic Salmon' },
    { value: 'tilapia', label: 'Nile Tilapia' },
    { value: 'trout', label: 'Rainbow Trout' },
    { value: 'bass', label: 'Sea Bass' },
    { value: 'shrimp', label: 'White Shrimp' }
  ];

  const feedOptions = [
    { value: 'pellets', label: 'Premium Pellets' },
    { value: 'flakes', label: 'Fish Flakes' },
    { value: 'live', label: 'Live Feed' },
    { value: 'frozen', label: 'Frozen Feed' }
  ];

  return (
    <div style={{ padding: '32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
          FormField Component Demo
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Enhanced form fields for aquaculture management forms
        </p>
      </div>

      {/* Aquaculture Facility Registration Form */}
      <div style={{ marginBottom: '48px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Facility Registration Form
        </h3>
        <div style={{ 
          maxWidth: '600px',
          padding: '24px',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <FormField
              id="farm-name"
              label="Facility Name"
              value={formData.farmName}
              placeholder="Enter your facility name"
              icon="home"
              required
              error={errors.farmName}
              onChange={handleFieldChange('farmName')}
            />

            <FormField
              id="email"
              label="Email Address"
              type="email"
              value={formData.email}
              placeholder="your-email@example.com"
              icon="user"
              required
              error={errors.email}
              hint="We'll use this for important notifications"
              onChange={handleFieldChange('email')}
            />

            <FormField
              id="password"
              label="Password"
              type="password"
              value={formData.password}
              placeholder="Create a secure password"
              required
              error={errors.password}
              onChange={handleFieldChange('password')}
            />

            <FormField
              id="phone"
              label="Phone Number"
              type="tel"
              value={formData.phone}
              placeholder="+1 (555) 123-4567"
              icon="call"
              hint="Optional: For emergency contact"
              onChange={handleFieldChange('phone')}
            />

            <FormField
              id="species"
              label="Primary Species"
              type="select"
              value={formData.species}
              placeholder="Select your main species"
              options={speciesOptions}
              icon="biomas"
              required
              onChange={handleFieldChange('species')}
            />

            <FormField
              id="water-temp"
              label="Water Temperature"
              type="number"
              value={formData.waterTemp}
              placeholder="24.5"
              icon="water"
              hint="Current water temperature in Celsius"
              onChange={handleFieldChange('waterTemp')}
            />

            <FormField
              id="description"
              label="Facility Description"
              type="textarea"
              value={formData.description}
              placeholder="Tell us about your aquaculture operation..."
              rows={4}
              hint="Describe your facility size, location, and production goals"
              onChange={handleFieldChange('description')}
            />

            <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
              <button
                onClick={validateForm}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #08B8D3 0%, #045F6D 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Validate Form
              </button>
              <button
                onClick={() => {
                  setFormData({
                    farmName: '',
                    email: '',
                    password: '',
                    phone: '',
                    waterTemp: '',
                    species: '',
                    description: '',
                    feedType: ''
                  });
                  setErrors({});
                }}
                style={{
                  padding: '12px 24px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Size Variants */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Size Variants
        </h3>
        <div style={{ display: 'grid', gap: '16px', maxWidth: '400px' }}>
          <FormField
            id="small-field"
            label="Small Field"
            size="sm"
            placeholder="Small input field"
            icon="settings"
          />
          
          <FormField
            id="medium-field"
            label="Medium Field"
            size="md"
            placeholder="Medium input field"
            icon="settings"
          />
          
          <FormField
            id="large-field"
            label="Large Field"
            size="lg"
            placeholder="Large input field"
            icon="settings"
          />
        </div>
      </div>

      {/* Variant Styles */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Style Variants
        </h3>
        <div style={{ display: 'grid', gap: '16px', maxWidth: '400px' }}>
          <FormField
            id="default-variant"
            label="Default Style"
            variant="default"
            placeholder="Default border style"
            icon="home"
          />
          
          <FormField
            id="filled-variant"
            label="Filled Style"
            variant="filled"
            placeholder="Filled background style"
            icon="database"
          />
          
          <FormField
            id="bordered-variant"
            label="Bordered Style"
            variant="bordered"
            placeholder="Bold border style"
            icon="dashboard-chart"
          />
        </div>
      </div>

      {/* Field States */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Field States
        </h3>
        <div style={{ display: 'grid', gap: '16px', maxWidth: '400px' }}>
          <FormField
            id="normal-state"
            label="Normal State"
            placeholder="This is a normal field"
            icon="tasks"
          />
          
          <FormField
            id="error-state"
            label="Error State"
            placeholder="Field with error"
            icon="bell"
            error="This field has an error message"
          />
          
          <FormField
            id="disabled-state"
            label="Disabled State"
            placeholder="This field is disabled"
            icon="settings"
            disabled
          />
          
          <FormField
            id="with-hint"
            label="With Hint"
            placeholder="Field with helpful hint"
            icon="chat"
            hint="This is a helpful hint for the user"
          />
        </div>
      </div>

      {/* Input Types */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Input Types
        </h3>
        <div style={{ display: 'grid', gap: '16px', maxWidth: '400px' }}>
          <FormField
            id="text-input"
            label="Text Input"
            type="text"
            placeholder="Enter text"
            icon="home"
          />
          
          <FormField
            id="email-input"
            label="Email Input"
            type="email"
            placeholder="user@example.com"
            icon="user"
          />
          
          <FormField
            id="password-input"
            label="Password Input"
            type="password"
            placeholder="Enter password"
          />
          
          <FormField
            id="number-input"
            label="Number Input"
            type="number"
            placeholder="Enter number"
            icon="dashboard-chart"
          />
          
          <FormField
            id="select-input"
            label="Select Input"
            type="select"
            options={feedOptions}
            icon="feed"
          />
          
          <FormField
            id="textarea-input"
            label="Textarea Input"
            type="textarea"
            placeholder="Enter multiple lines of text..."
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};

export default FormFieldDemo;